/**
 * Game Overlay
 *
 * In-game UI: END TURN button, instruction text, current player indicator.
 *
 * @module ui/GameOverlay
 */

import { useGameStore } from './hooks/useGameStore.js';
import { SeatSwatch } from './SeatSwatch.jsx';
import { playerName } from '../store/GameStore.js';
import {
  PLAYER_COLORS_CSS,
  COLORBLIND_PLAYER_COLORS_CSS,
  HUD_BAR_HEIGHT,
  HUD_BAR_HEIGHT_VAR,
} from '../renderer/constants.js';

/*
 * END TURN cannot borrow `.dw-btn`'s focus ring: that ring is `--ui-accent`,
 * which is exactly this button's background, so it would be invisible on the
 * control a keyboard player tabs onto off the end of the board (#201). Its own
 * ring is drawn in the text color instead. :focus-visible, like the rest of the
 * chrome, so a mouse click leaves no ring behind.
 */
const OVERLAY_CSS = `
.dw-end-turn:focus-visible {
  outline: 3px solid var(--ui-text);
  outline-offset: 3px;
}
.dw-end-turn[aria-disabled='true'] { opacity: .55; cursor: default; }
`;

const STYLE = {
  /*
   * The strip stops at the top of the HUD bar, so END TURN and the instruction
   * line sit just above the chips rather than on them. The bar's height is not
   * a constant any more — under 560px it goes to two rows so eight seats fit
   * (#222) — so the stop follows the HUD_BAR_HEIGHT_VAR the mounted GameHUD
   * measures and publishes, and falls back to HUD_BAR_HEIGHT itself (the same
   * constant GameRenderer reserves with) for any render with no HUD in the DOM.
   */
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: `var(${HUD_BAR_HEIGHT_VAR}, ${HUD_BAR_HEIGHT}px)`,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: '1rem',
    pointerEvents: 'none',
  },
  /*
   * The instruction line sits directly on the live board, so it carries the
   * ink-rim halo as its own backing rather than a fixed dark smudge: the rim
   * flips with the theme (a pale rim behind dark text on the light board),
   * where `1px 1px 4px rgba(0,0,0,0.8)` only ever worked under white (#220).
   */
  message: {
    fontFamily: 'Roboto, sans-serif',
    fontSize: '1rem',
    color: 'var(--ui-text)',
    textShadow: 'var(--ui-text-halo)',
    marginBottom: '0.5rem',
    textAlign: 'center',
  },
  /*
   * White on the accent fill is a deliberate literal, not a missed token. END
   * TURN is 1.3rem Anton (~21px bold), so WCAG's large-text 3:1 is the bar,
   * and white clears it on both accents (dark 3.83:1, light 5.81:1) — pinned
   * in GameOverlay.test.js. A `--ui-on-accent` token would carry the same
   * value in both themes and buy nothing (#220).
   */
  endTurnBtn: {
    fontFamily: 'Anton, sans-serif',
    fontSize: '1.3rem',
    padding: '0.5rem 2rem',
    background: 'var(--ui-accent)',
    border: 'none',
    color: '#fff',
    cursor: 'pointer',
    borderRadius: '6px',
    letterSpacing: '0.05em',
    pointerEvents: 'auto',
  },
  /* Floats straight on the live board with no panel under it — and until #220
     carried no shadow at all — so it takes the same ink rim the rest of the
     over-the-board text uses (composeTextHalo): its legibility must not depend
     on which territory drifts beneath it. */
  thinking: {
    fontFamily: 'Anton, sans-serif',
    fontSize: '1.2rem',
    color: 'var(--ui-text-muted)',
    textShadow: 'var(--ui-text-halo)',
    marginBottom: '0.5rem',
  },
  /* The bot's name, a step brighter than the muted "is thinking..." around it. */
  thinkingName: { color: 'var(--ui-text)' },
};

/**
 * @param {Object} props
 * @param {Object} props.store - GameStore instance
 * @param {() => void} props.onEndTurn
 */
export function GameOverlay({ store, onEndTurn }) {
  const gameState = useGameStore(store, s => s.gameState);
  const awaitingInput = useGameStore(store, s => s.awaitingInput);
  const humanPlayerIndex = useGameStore(store, s => s.humanPlayerIndex);
  const playerNames = useGameStore(store, s => s.playerNames);
  const prefs = useGameStore(store, s => s.preferences);
  /*
   * The controller's verdict, not this component's. It derives the dead-end
   * from the engine's own `getValidMoves` wherever it refreshes the board hints
   * and publishes it as `noValidMoves` (#196: one owner for that derivation, so
   * the board, the prompt and the rules cannot drift apart). Reading the engine
   * here — as this used to — made the overlay a second, independent authority
   * on the same question, and paid for a full scan of the board on every render
   * of a line that changes once a turn.
   */
  const noAttacks = useGameStore(store, s => s.noValidMoves);

  if (!gameState) return null;

  const colorPalette = prefs?.colorBlindMode ? COLORBLIND_PLAYER_COLORS_CSS : PLAYER_COLORS_CSS;
  const currentPlayerId = gameState.turnOrder[gameState.currentPlayerIndex];
  const isHumanTurn = currentPlayerId === humanPlayerIndex;
  // A roll is in flight: nothing is being awaited, so END TURN does nothing.
  const endTurnUnavailable = awaitingInput === null;

  return (
    <div style={STYLE.overlay}>
      <style>{OVERLAY_CSS}</style>
      {isHumanTurn && awaitingInput === 'selectFrom' && (
        <p style={STYLE.message}>
          {noAttacks
            ? 'No attacks available. End your turn to reinforce.'
            : 'Click your territory to attack from'}
        </p>
      )}
      {isHumanTurn && awaitingInput === 'selectTo' && (
        <p style={STYLE.message}>Click a neighbor to attack</p>
      )}
      {/* The opponent by name ("Conqueror is thinking..."), with its seat as a
          swatch in front: the name gives each rival an identity and the seat
          color tells two seats running the same bot apart, but the color rides
          beside the words rather than in them — as text a pastel seat was
          unreadable on the light theme's board (#220). The name is set in the
          text color to keep it ahead of the muted "is thinking...". */}
      {!isHumanTurn && humanPlayerIndex !== null && (
        <p style={STYLE.thinking}>
          <SeatSwatch color={colorPalette[currentPlayerId % colorPalette.length]} />
          <span style={STYLE.thinkingName}>{playerName(playerNames, currentPlayerId)}</span> is
          thinking...
        </p>
      )}
      {/* Last in the playing screen's tab order: App renders BoardFocus — the
          human's own territories, as real buttons — immediately before this
          overlay, so Tab past the last of them lands here and Shift+Tab goes
          back onto the board (#201, #211). E is the shortcut past that walk;
          the title advertises it the way QUIT advertises Esc. */}
      {/* `aria-disabled`, never `disabled`. The button is unavailable only
          while a roll is in flight — a second or two, mid-turn — and a real
          `disabled` attribute pulls it out of the tab order for exactly that
          window: a keyboard player tabbing off the end of the board during an
          animation would find nothing there, and their focus would be thrown
          to the top of the page. The aria form says the same thing to a screen
          reader while keeping the control reachable. The click is a no-op here
          and guarded again in the controller. */}
      {/* The shortcut is advertised only while it works. `aria-keyshortcuts`
          on an `aria-disabled` control tells a screen reader that E does
          something here, and during the roll it does not — the controller
          ignores it for the same second or two the click is a no-op — so both
          the hint and the tooltip stand down with the button. */}
      {isHumanTurn && (
        <button
          className="dw-end-turn"
          style={STYLE.endTurnBtn}
          onClick={endTurnUnavailable ? undefined : onEndTurn}
          aria-disabled={endTurnUnavailable ? 'true' : undefined}
          title={endTurnUnavailable ? undefined : 'End turn (E)'}
          aria-keyshortcuts={endTurnUnavailable ? undefined : 'E'}
        >
          END TURN
        </button>
      )}
    </div>
  );
}
